"use client";

import { useEffect, useRef, useState } from "react";
import type { HTMLAttributes, PropsWithChildren } from "react";

import ImgBgResponsiveRender from "./ImgBgResponsiveRender";

function unmount(triggers: string[], handleListener: () => void) {
  try {
    for (const trigger of triggers) {
      window.removeEventListener(trigger, handleListener);
    }
  } catch (error) {
    console.error("error unmounting triggers", error, triggers);
  }
}

interface ImgBgResponsiveProps extends HTMLAttributes<HTMLElement> {
  imageUrl: string;
  lazy?: "eager" | "lazy";
  prefetch?: boolean;
  triggers?: string[];
  forceFallback?: boolean;
}

function ImgBgResponsiveLazy({
  lazy = "lazy",
  children,
  imageUrl,
  className,
  prefetch = true,
  forceFallback = false,
  style,
  triggers,
  ...attrs
}: PropsWithChildren<ImgBgResponsiveProps>) {
  const lazyRef = useRef<any>();
  const [loadImage, setLoadImage] = useState<boolean>(false);

  useEffect(() => {
    //no lazy load
    if (lazy === "eager" || loadImage) {
      return;
    }

    //lazy load based on triggers
    const handleListener = () => {
      setLoadImage(true);
      unmount(triggers ?? [], handleListener);
    };

    if ((triggers ?? []).length > 0) {
      for (const trigger of triggers ?? []) {
        window.addEventListener(trigger, handleListener, { passive: true });
      }
      return () => {
        unmount(triggers ?? [], handleListener);
      };
    }

    function fallbackLoader() {
      setLoadImage(true);
    }

    if ("IntersectionObserver" in window && !forceFallback) {
      const imageObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              setLoadImage(true);
              imageObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "-120px 0px 0px 0px" }
      );
      imageObserver.observe(lazyRef.current);
    } else {
      window.addEventListener("scroll", fallbackLoader, { passive: true });
      return () => {
        window.removeEventListener("scroll", fallbackLoader);
      };
    }
  }, [lazy, loadImage, triggers, forceFallback]);

  return (
    <ImgBgResponsiveRender
      ref={lazyRef}
      imageUrl={imageUrl}
      lazy={lazy}
      prefetch={prefetch}
      style={style}
      loadImage={loadImage}
      className={className}
      {...attrs}
    >
      {children}
    </ImgBgResponsiveRender>
  );
}

export default ImgBgResponsiveLazy;
